'use client';

import { useRef } from 'react';

import Arrow from '@/components/ui/Arrow';
import CarouselDots from '@/components/ui/CarouselDots';
import { useCarousel } from '@/hooks/useCarousel';
import { COMMON_I18N } from '@/lib/i18n/commonI18n';
import type { AppLocale } from '@/lib/i18n/locales';
import type { GoogleReview } from '@/lib/google/getGoogleReviews';
import ReviewCard from './ReviewCard';

const ReviewCarousel = ({ reviews, locale, label }: ReviewCarouselProps) => {
  const t = COMMON_I18N[locale];
  const viewportRef = useRef<HTMLDivElement | null>(null);

  const {
    emblaRef,
    selectedIndex,
    canPrev,
    canNext,
    snapCount,
    scrollPrev,
    scrollNext,
    scrollTo,
  } = useCarousel({ loop: true });

  return (
    <div className="relative">
      <div className="relative">
        <div className="pointer-events-none absolute inset-x-0 top-0 bottom-0 z-10">
          <Arrow
            direction="prev"
            label={t.carouselPrevious}
            disabled={!canPrev}
            onClick={scrollPrev}
            className="hidden tablet:flex -left-4 pointer-events-auto"
          />
          <Arrow
            direction="next"
            label={t.carouselNext}
            disabled={!canNext}
            onClick={scrollNext}
            className="hidden tablet:flex -right-4 pointer-events-auto"
          />
        </div>

        <div
          aria-roledescription="carousel"
          aria-label={label}
          className="overflow-hidden"
          ref={(node) => {
            viewportRef.current = node;
            emblaRef(node);
          }}
        >
          <div className="-mx-[10px] flex items-stretch">
            {reviews.map((review, i) => (
              <div
                key={i}
                role="group"
                aria-roledescription="slide"
                aria-label={`${i + 1} ${t.slideOf} ${reviews.length}`}
                className="box-border shrink-0 basis-full px-[10px] min-w-0 tablet:basis-1/2 desktop:basis-1/3"
              >
                <div className="w-full min-w-0 h-full">
                  <ReviewCard
                    review={review}
                    index={i}
                    ariaLabelStars={t.starsAriaLabel(review.rating)}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-center">
        <CarouselDots
          count={snapCount}
          selectedIndex={selectedIndex}
          onSelect={scrollTo}
          label={label}
          slideOf={t.slideOf}
        />
      </div>
    </div>
  );
};

export default ReviewCarousel;

type ReviewCarouselProps = {
  reviews: GoogleReview[];
  locale: AppLocale;
  label: string;
};
